/**
 * useUnifiedShell — is the buyer information architecture on?
 *
 * **It always is. For everyone. That is the whole hook.** (SYN-2517)
 *
 * Maria, 17 Aug 2026, verbatim: *"they're the same for anyone, internal or
 * external"*, *"same journey"*, *"synapsedx just doesn't have to pay"*. The
 * customer journey is one journey. Nothing about which org you are, or whether
 * you pay, may decide which product you see.
 *
 * ## What this replaced, and why it was broken
 *
 * The hook used to return `allowlisted || status?.buyerIaEligible`, and BOTH
 * halves are false for a stranger who signs herself up:
 *
 *   1. `VITE_UNIFIED_SHELL_ORG_IDS` (SYN-1503) is inlined into the SPA by Vite
 *      at **build time**, so it can never name an org created after the image
 *      was built. A self-serve org is unallowlistable by construction, not by
 *      oversight. SYN-2022 recognised this and added the second half rather
 *      than removing the first.
 *   2. `buyerIaEligible` (SYN-2022) means the org has an **active
 *      subscription**. Payment is a billing fact. It was standing in for a
 *      product decision.
 *
 * Net effect: Home, Routines, Activity and My Setup (page title "Customize",
 * `Layout.tsx:196`) were unreachable for every customer who ever signed
 * themselves up, and on cloch neither variable
 * is even passed to the build (see `.github/workflows/deploy-cloch.yml`), so
 * the answer was false for internal users too.
 *
 * ## Why unconditional and not "signed in"
 *
 * Gating on Clerk's `isSignedIn` would be false while Clerk loads, and
 * `routesFor()` keys `RouterProvider` on this value — a transient false
 * mounts the legacy route table and then re-mounts the whole router. There is
 * nothing to protect by doing that: `authRoutes` is spread into BOTH route
 * tables (`src/routes.tsx`), so a signed-out visitor lands on `/sign-in`
 * either way, and every other route is behind `ProtectedPage`/`AuthGuard`.
 * Authentication is enforced at the route, not by the shape of the shell.
 *
 * ## What is deliberately still standing
 *
 * The two route tables themselves (`legacyRoutes` / `unifiedRoutes`) and
 * `lib/unified-shell.ts`'s pure `deriveUnifiedShell` are NOT deleted here.
 * Removing the fork is SYN-2518, a careful deletion across 16 files including
 * nine test files. A half-deleted fork is worse than a flagged one.
 *
 * Genuinely staff-only surfaces are untouched and are a different question:
 * `isSynapsedxConsoleEnabled` and `useIsPlatformStaff` gate an internal
 * console that is not part of the customer journey.
 */

export function useUnifiedShell(): boolean {
  return true;
}
